import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { InstagramLogo, TiktokLogo, Play, DownloadSimple, ShareNetwork, DeviceMobile, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HighlightModule = ({ event }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [maxDuration, setMaxDuration] = useState(30);
  const [clipCount, setClipCount] = useState(10);
  const [photoDuration, setPhotoDuration] = useState(2.5);
  const [library, setLibrary] = useState([]);
  const [libraryPickId, setLibraryPickId] = useState('');
  const videoRef = useRef(null);

  const hlPurchased = event.highlight_purchased;

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, [event.event_id]);

  useEffect(() => {
    axios.get(`${API}/music/library`).then(r => setLibrary(r.data || [])).catch(() => {});
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/events/${event.event_id}/highlight/status`);
      setStatus(res.data);
    } catch {}
  };

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/highlight/purchase`, {});
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
    } finally { setPurchasing(false); }
  };

  const handleGenerate = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      toast.error('Please log in to generate your highlight reel');
      navigate('/login');
      return;
    }
    setGenerating(true);
    try {
      await axios.post(`${API}/events/${event.event_id}/highlight/generate`, {
        max_duration: maxDuration,
        clip_count: clipCount,
        photo_duration: photoDuration,
        music_library_id: libraryPickId || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Highlight reel generation started!');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setGenerating(false); }
  };

  const getVideoUrl = () => {
    const u = status?.video_url;
    return u ? (u.startsWith('/api') ? `${BACKEND_URL}${u}` : u) : '';
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(`${API}/events/${event.event_id}/highlight/download`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${event.event_name.replace(/\s/g, '_')}_highlight.mp4`;
      link.click();
      toast.success('Downloading...');
    } catch { toast.error('Download failed'); }
  };

  const hlStatus = status?.status || event.highlight?.status || 'none';

  // NOT PURCHASED
  if (!hlPurchased && hlStatus === 'none') {
    return (
      <div className="rounded-2xl mb-8 overflow-hidden border border-[#B89762]/50/20" style={{ background: 'linear-gradient(135deg, #0F0723 0%, #1A0530 50%, #0D0D0D 100%)' }} data-testid="highlight-module-purchase">
        <div className="p-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex gap-1.5">
              <InstagramLogo size={22} weight="bold" className="text-[#B89762]" />
              <TiktokLogo size={22} weight="bold" className="text-white" />
            </div>
            <h3 className="font-bold text-xl text-white">Highlight Reel</h3>
            <span className="px-2 py-0.5 rounded-full bg-[#B89762]/20 text-[#B89762] text-xs font-bold">SOCIAL</span>
          </div>
          <p className="text-gray-300 mb-1 font-medium">A shareable highlight video for Instagram & TikTok</p>
          <p className="text-gray-500 text-sm mb-5">20-30 seconds. Vertical. Made for sharing.</p>

          <div className="grid grid-cols-2 gap-2 mb-5">
            {['Vertical 9:16 format', 'Fast-cut energy edit', 'Perfect for Stories', 'Instant download'].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 bg-[#B89762]/30">
                  <Check size={8} weight="bold" className="text-[#C29D52]" />
                </div>
                {f}
              </div>
            ))}
          </div>

          <button
            onClick={handlePurchase}
            disabled={purchasing}
            className="w-full py-3.5 rounded-xl font-bold text-white text-base disabled:opacity-50 hover:scale-[1.02] transition-all"
            style={{ background: 'linear-gradient(135deg, #B89762, #A855F7)' }}
            data-testid="purchase-highlight-btn"
          >
            {purchasing ? 'Redirecting...' : 'Add Highlight Reel — £12'}
          </button>
        </div>
      </div>
    );
  }

  // PURCHASED, NOT GENERATED
  if (hlStatus === 'none' || hlStatus === 'failed') {
    return (
      <div className="rounded-2xl mb-8 overflow-hidden" style={{ background: '#0F0F10' }} data-testid="highlight-module-create">
        <div className="p-8">
          <div className="flex items-center gap-2 mb-2">
            <DeviceMobile size={22} weight="bold" className="text-[#B89762]" />
            <h3 className="font-bold text-xl text-white">Highlight Reel</h3>
          </div>
          <p className="text-gray-400 mb-5 text-sm">Ready to create your social-ready highlight video?</p>
          {hlStatus === 'failed' && (
            <div className="mb-4 p-3 rounded-lg bg-teal-500/10 border border-teal-500/20 text-teal-300 text-sm" data-testid="highlight-failed-banner">
              ⚡ Your Photo Slideshow is ready. Tap <b>Create Highlight Reel</b> below to try the social-ready Highlight Reel again.
            </div>
          )}

          {/* Music Library picker */}
          {library.length > 0 && (
            <div className="mb-4 p-4 rounded-xl bg-[#15161B]/40 border border-white/5" data-testid="hl-music-library-section">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Soundtrack (optional)</span>
                {libraryPickId && (
                  <button onClick={() => setLibraryPickId('')} className="text-xs text-gray-400 hover:text-white" data-testid="hl-clear-library-pick">Clear</button>
                )}
              </div>
              <select
                value={libraryPickId}
                onChange={(e) => setLibraryPickId(e.target.value)}
                className="w-full bg-[#15161B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="hl-music-library-select"
              >
                <option value="">— Use upbeat default (or your custom track) —</option>
                {Object.entries(library.reduce((acc, t) => {
                  const g = t.mood || 'upbeat'; acc[g] = acc[g] || []; acc[g].push(t); return acc;
                }, {})).map(([mood, tracks]) => (
                  <optgroup key={mood} label={mood.charAt(0).toUpperCase() + mood.slice(1)}>
                    {tracks.map((t) => (
                      <option key={t.track_id} value={t.track_id}>
                        {t.name}{t.duration_sec ? ` · ${Math.round(t.duration_sec)}s` : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {libraryPickId && (() => {
                const t = library.find(x => x.track_id === libraryPickId);
                if (!t) return null;
                return <audio controls src={`${API}/music/library/${t.track_id}/stream`} className="w-full h-8 mt-2" />;
              })()}
            </div>
          )}

          {/* Advanced clip controls */}
          <div className="mb-5">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-white mb-3"
              data-testid="toggle-hl-advanced"
            >
              Advanced {showAdvanced ? '▴' : '▾'}
            </button>
            {showAdvanced && (
              <div className="space-y-4 p-4 rounded-xl bg-[#15161B]/40 border border-white/5" data-testid="hl-advanced-controls">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-gray-400">Reel length</label>
                    <span className="text-xs font-mono text-white">{maxDuration}s</span>
                  </div>
                  <input type="range" min="15" max="60" step="5" value={maxDuration}
                         onChange={(e) => setMaxDuration(parseInt(e.target.value, 10))}
                         className="w-full accent-[#B89762]" data-testid="hl-max-duration" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-gray-400">Number of clips</label>
                    <span className="text-xs font-mono text-white">{clipCount}</span>
                  </div>
                  <input type="range" min="5" max="15" step="1" value={clipCount}
                         onChange={(e) => setClipCount(parseInt(e.target.value, 10))}
                         className="w-full accent-[#B89762]" data-testid="hl-clip-count" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-gray-400">Per-photo duration</label>
                    <span className="text-xs font-mono text-white">{photoDuration.toFixed(1)}s</span>
                  </div>
                  <input type="range" min="1.5" max="4" step="0.5" value={photoDuration}
                         onChange={(e) => setPhotoDuration(parseFloat(e.target.value))}
                         className="w-full accent-[#B89762]" data-testid="hl-photo-duration" />
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full py-3.5 rounded-xl font-bold text-white text-base disabled:opacity-50 hover:scale-[1.02] transition-all"
            style={{ background: 'linear-gradient(135deg, #B89762, #A855F7)' }}
            data-testid="generate-highlight-btn"
          >
            {generating ? 'Starting...' : 'Create Highlight Reel'}
          </button>
          <p className="text-center text-gray-600 text-xs mt-2">Takes about 2 minutes</p>
        </div>
      </div>
    );
  }

  // PROCESSING
  if (hlStatus === 'processing') {
    const progress = status?.progress || 0;
    return (
      <div className="rounded-2xl mb-8 overflow-hidden" style={{ background: '#0F0F10' }} data-testid="highlight-module-processing">
        <div className="p-8 text-center">
          <DeviceMobile size={32} weight="bold" className="text-[#B89762] mx-auto mb-3 animate-pulse" />
          <h3 className="font-bold text-lg text-white mb-2">Creating your highlight reel...</h3>
          <div className="w-full h-2 bg-gray-800 rounded-full mb-3 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #B89762, #A855F7)' }} />
          </div>
          <p className="text-gray-500 text-sm">Usually under 2 minutes</p>
        </div>
      </div>
    );
  }

  // READY
  if (hlStatus === 'ready') {
    return (
      <div className="rounded-2xl mb-8 overflow-hidden" style={{ background: '#0F0F10' }} data-testid="highlight-module-ready">
        <div className="p-8">
          <div className="flex items-center gap-2 mb-4">
            <DeviceMobile size={22} weight="bold" className="text-[#B89762]" />
            <h3 className="font-bold text-lg text-white">Your Highlight Reel</h3>
            {status?.duration && <span className="text-gray-500 text-sm ml-auto">{status.duration}</span>}
          </div>
          {/* Vertical video preview */}
          <div className="flex justify-center mb-5">
            <div className="relative rounded-2xl overflow-hidden bg-black shadow-xl" style={{ width: 200, height: 356 }}>
              <video ref={videoRef} src={getVideoUrl()} controls className="w-full h-full object-cover" data-testid="highlight-video-player" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={handleDownload} className="py-3 rounded-xl border border-gray-600 text-white font-semibold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2" data-testid="download-highlight-btn">
              <DownloadSimple size={18} weight="bold" /> Download
            </button>
            <button onClick={() => { navigator.clipboard.writeText(getVideoUrl()); toast.success('Link copied!'); }} className="py-3 rounded-xl border border-gray-600 text-white font-semibold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2" data-testid="share-highlight-btn">
              <ShareNetwork size={18} weight="bold" /> Share
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default HighlightModule;
